// ./src/utils/api/QrTagService.ts
//
// ROADMAP #172 — QR tags: codes on an event, GENERATED here or ADOPTED from cards / bracelets printed elsewhere, linked at
// the door to a guest or a ticket holder (a person may hold several), scanned later. OpResult-native, event-scoped.
// ⚠ Not `TagService` — that is the account's catalog of LABELS.
//
//   list / listAll / get / create / update / remove / removeMany / unlinkMany — the QR tags page (owner, or crew with
//     event.operate); `total` on the list result.
//   generate — N new tags (Pro and Business).
//   setLinks — THE DOOR: a party's tags in one all-or-nothing save; refusals keyed `items[<index>].code`. A code that is
//     not a tag yet is adopted (Pro and Business). Owner, or crew with check-in.
//   resolve — what a scanned code is on this event: the tag and its holder, or nothing.
//   #172 phase 2 — a GENERATED tag can be PUBLIC: anyone's phone camera opens its page (`publicUrl`, what its QR carries)
//     with the host's title and message, plus the holder details the host offers (`publicFields`) that the holder agreed
//     to (`publicAgreed` — read only here; the holder chooses on the event website or from their email).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// Mirrors eventsmagic-api boundary/qrTag.ts.
export const QR_TAG_ORIGIN = {
  GENERATED: "generated",
  ADOPTED: "adopted",
} as const;

export const QR_TAG_TARGET_KIND = {
  GUEST: "guest",
  TICKET: "ticket",
} as const;

export type QrTagTargetKind = (typeof QR_TAG_TARGET_KIND)[keyof typeof QR_TAG_TARGET_KIND];

/** One "Generate tags" run (the server refuses more). */
export const QR_TAG_GENERATE_MAX = 500;

/** Mirrors eventsmagic-api boundary/qrTag.ts — the holder details a public tag may offer; a question is `q:<id>`. */
export const QR_TAG_PUBLIC_FIELD = {
  FIRST_NAME: "first_name",
  FULL_NAME: "full_name",
  PHONE: "phone",
} as const;

export const QR_TAG_PUBLIC_QUESTION_PREFIX = "q:";
export const QR_TAG_PUBLIC_TITLE_MAX = 128;
export const QR_TAG_PUBLIC_MESSAGE_MAX = 2000;
/** The question types a public tag can show (a value or a choice — never a file, a picture or an address). */
export const QR_TAG_OFFERABLE_QUESTION_TYPES = ["single_value", "single_choice", "multi_choice"];

// The scan reader lives on its own (a guard runs it with no API client): see ./qrTagScan.
export { qrTagCodeFromScan } from "./qrTagScan";

const QR_TAG_FIELDS = `
  id
  accountId
  eventId
  eventTimeSlotId
  code
  origin
  sequenceNo
  label
  notes
  targetKind
  targetId
  holder { kind id name code inactive }
  linkedAt
  linkedByLabel
  linkedServicePointId
  isPublic
  publicTitle
  publicMessage
  publicFields
  publicConsentEventPolicyId
  publicAgreed
  publicAgreedByPolicyAt
  publicConsentAt
  publicNotifiedAt
  publicUrl
  status
  rights
  createdAt
  updatedAt
`;

const QR_TAG_LIST_QUERY = gql`
  query QrTagList($filter: QrTagFilter, $params: PaginationAndSorting) {
    qrTagList(filter: $filter, params: $params) {
      code
      total
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_GET_QUERY = gql`
  query QrTagGet($id: ID) {
    qrTagGet(id: $id) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_RESOLVE_QUERY = gql`
  query QrTagResolve($params: QrTagResolveInput) {
    qrTagResolve(params: $params) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_CREATE_MUTATION = gql`
  mutation QrTagCreate($params: QrTagInput) {
    qrTagCreate(params: $params) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_UPDATE_MUTATION = gql`
  mutation QrTagUpdate($where: QrTagWhereInput, $params: QrTagInput) {
    qrTagUpdate(where: $where, params: $params) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_REMOVE_MUTATION = gql`
  mutation QrTagRemove($where: QrTagWhereInput) {
    qrTagRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_GENERATE_MUTATION = gql`
  mutation QrTagGenerate($params: QrTagGenerateInput) {
    qrTagGenerate(params: $params) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const QR_TAG_SET_LINKS_MUTATION = gql`
  mutation QrTagSetLinks($params: QrTagSetLinksInput) {
    qrTagSetLinks(params: $params) {
      code
      data { ${QR_TAG_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export interface QrTagHolder {
  kind?: QrTagTargetKind | string | null;
  id?: string | null;
  name?: string | null;
  /** The holder's OWN pass code (guest code / ticket code) — what the door checks in. */
  code?: string | null;
  /** Removed, or a ticket no longer live: the tag points at nobody who can come in. */
  inactive?: boolean | null;
}

export interface QrTag {
  id?: string;
  accountId?: string;
  eventId?: string;
  eventTimeSlotId?: string | null;
  code?: string;
  origin?: string;
  sequenceNo?: number | null;
  label?: string | null;
  notes?: string | null;
  targetKind?: string | null;
  targetId?: string | null;
  holder?: QrTagHolder | null;
  linkedAt?: string | null;
  linkedByLabel?: string | null;
  linkedServicePointId?: string | null;
  /** #172 phase 2 — anyone's phone camera opens its page (generated tags only). */
  isPublic?: boolean | null;
  publicTitle?: string | null;
  publicMessage?: string | null;
  /** The holder details the host OFFERS: QR_TAG_PUBLIC_FIELD values and `q:<question id>`. */
  publicFields?: string[] | null;
  /** The event policy whose acceptance at registration agrees to the whole offer; null = each holder chooses. */
  publicConsentEventPolicyId?: string | null;
  /** The offered details the CURRENT holder agreed to show. */
  publicAgreed?: string[] | null;
  /** When the current holder accepted that policy ('' = time unknown); null = not by policy. */
  publicAgreedByPolicyAt?: string | null;
  publicConsentAt?: string | null;
  publicNotifiedAt?: string | null;
  /** What a generated tag's QR carries — its public page. Null for an adopted code. */
  publicUrl?: string | null;
  status?: number;
  rights?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface QrTagInput {
  /** Create-only. */
  eventId?: string;
  /** Create-only: a code typed or scanned (adopted). Omit to have one generated. */
  code?: string | null;
  label?: string | null;
  notes?: string | null;
  eventTimeSlotId?: string | null;
  /** With targetId; both null unlinks. */
  targetKind?: string | null;
  targetId?: string | null;
  isPublic?: boolean | null;
  publicTitle?: string | null;
  publicMessage?: string | null;
  publicFields?: string[] | null;
  publicConsentEventPolicyId?: string | null;
}

export interface QrTagFilter {
  id?: string[];
  eventId?: string[];
  eventTimeSlotId?: string[];
  origin?: string[];
  targetKind?: string[];
  targetId?: string[];
  /** true: held by somebody; false: held by nobody. */
  linked?: boolean | null;
  isPublic?: boolean | null;
  searchKeyword?: string;
}

export interface QrTagWhereInput {
  id?: string | string[];
}

export interface QrTagGenerateInput {
  eventId: string;
  quantity: number;
  label?: string | null;
  eventTimeSlotId?: string | null;
  isPublic?: boolean | null;
  publicTitle?: string | null;
  publicMessage?: string | null;
  publicFields?: string[] | null;
  publicConsentEventPolicyId?: string | null;
}

/** One tag handed to one person at the door. */
export interface QrTagLinkItem {
  targetKind: QrTagTargetKind | string;
  targetId: string;
  code: string;
}

export class QrTagService {
  static async list(filter?: QrTagFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(QR_TAG_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** Every tag matching `filter` — for the print sheet and the CSV, which need the whole set. */
  static async listAll(filter?: QrTagFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<QrTag>(QrTagService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(QR_TAG_GET_QUERY, { id });
  }

  /** What a scanned code is on this event — the tag and its holder, or an empty list. */
  static async resolve(eventId: string, code: string): Promise<OpResult> {
    return ApiService.executeQuery(QR_TAG_RESOLVE_QUERY, { params: { eventId, code } });
  }

  static async create(params: QrTagInput): Promise<OpResult> {
    return ApiService.executeMutation(QR_TAG_CREATE_MUTATION, { params });
  }

  static async update(where: QrTagWhereInput, params: QrTagInput): Promise<OpResult> {
    return ApiService.executeMutation(QR_TAG_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(QR_TAG_REMOVE_MUTATION, { where: { id } });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(QR_TAG_REMOVE_MUTATION, { where: { id: ids } });
  }

  /** Unlink every selected tag in one call (a bulk edit may only UNLINK). */
  static async unlinkMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(QR_TAG_UPDATE_MUTATION, { where: { id: ids }, params: { targetKind: null, targetId: null } });
  }

  static async generate(params: QrTagGenerateInput): Promise<OpResult> {
    return ApiService.executeMutation(QR_TAG_GENERATE_MUTATION, { params });
  }

  /** The door: all or nothing; refusals keyed `items[<index>].code`. */
  static async setLinks(eventId: string, items: QrTagLinkItem[], servicePointId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(QR_TAG_SET_LINKS_MUTATION, { params: { eventId, items, servicePointId: servicePointId || null } });
  }
}
